import React, { useEffect, useState } from 'react';
import { Contact, Organization, PaginatedResponse } from '../../types';
import { createContact, updateContact, fetchOrganizations } from '../../services/api';

interface Props {
  contact?: Contact | null;
  onSaved: (c: Contact) => void;
  onClose: () => void;
}

const DESIGNATIONS = ['Proprietor', 'Owner', 'Manager', 'CEO', 'Partner', 'Director', 'Pharmacist', 'Other'];

const ContactForm: React.FC<Props> = ({ contact, onSaved, onClose }) => {
  const isEdit = !!contact;

  const [form, setForm] = useState({
    name:            contact?.name            ?? '',
    designation:     contact?.designation     ?? '',
    phone:           contact?.phone           ?? '',
    whatsapp_number: contact?.whatsapp_number ?? '',
    email:           contact?.email           ?? '',
    organization_id: contact?.organization_id?.toString() ?? '',
    is_primary:      contact?.is_primary      ?? false,
    notes:           contact?.notes           ?? '',
  });
  const [orgs, setOrgs]       = useState<Organization[]>([]);
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations({ per_page: 200 })
      .then((r: PaginatedResponse<Organization>) => setOrgs(r.data))
      .catch(() => {});
  }, []);

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const t = e.target as HTMLInputElement;
    const val = t.type === 'checkbox' ? t.checked : t.value;
    setForm(f => ({ ...f, [e.target.name]: val }));
    setErrors(errs => ({ ...errs, [e.target.name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setErrors({});
    try {
      const payload = {
        ...form,
        organization_id: form.organization_id ? parseInt(form.organization_id) : undefined,
      };
      const saved = isEdit && contact
        ? await updateContact(contact.id, payload)
        : await createContact(payload);
      onSaved(saved);
    } catch (err: any) {
      if (err?.response?.data?.errors) {
        const mapped: Record<string, string> = {};
        Object.entries(err.response.data.errors).forEach(([k, v]) => { mapped[k] = (v as string[])[0]; });
        setErrors(mapped);
      } else {
        alert('Failed to save contact');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="deal-modal">
        <div className="deal-modal-header">
          <div className="deal-modal-header-inner">
            <div>
              <h2 className="deal-modal-title">{isEdit ? 'Edit Contact' : 'New Contact'}</h2>
              <p className="deal-modal-subtitle">Contact details</p>
            </div>
            <button className="deal-modal-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="deal-modal-body">
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Full Name <span className="required">*</span></label>
              <input className={`deal-form-input${errors.name ? ' input-error' : ''}`} name="name" value={form.name} onChange={handle} required placeholder="Ramesh Kumar" />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Designation</label>
              <select className="deal-form-input" name="designation" value={form.designation} onChange={handle}>
                <option value="">Select…</option>
                {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Phone</label>
              <input className="deal-form-input" name="phone" value={form.phone} onChange={handle} placeholder="+91 98765 43210" />
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">WhatsApp</label>
              <input className="deal-form-input" name="whatsapp_number" value={form.whatsapp_number} onChange={handle} placeholder="+91 98765 43210" />
            </div>
          </div>

          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Email</label>
              <input className={`deal-form-input${errors.email ? ' input-error' : ''}`} type="email" name="email" value={form.email} onChange={handle} placeholder="ramesh@company.in" />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Organization</label>
              <select className="deal-form-input" name="organization_id" value={form.organization_id} onChange={handle}>
                <option value="">None</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          <div className="deal-form-group">
            <label className="deal-form-label">Notes</label>
            <textarea className="deal-form-input" name="notes" value={form.notes} onChange={handle} rows={3} placeholder="Any additional context…" />
          </div>

          <div className="deal-form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" name="is_primary" checked={form.is_primary}
              onChange={handle} style={{ width: 16, height: 16, accentColor: 'var(--primary)' }} />
            <label className="deal-form-label" style={{ margin: 0 }}>Primary contact for organization</label>
          </div>

          <div className="deal-modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="btn-spinner" />Saving…</> : isEdit ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
